import { DateKind, LabelElement, ThemeVariable, ThemeVariableType } from './types';

export const DATE_KIND_CONFIG: Record<DateKind, { name: string; key: string }> = {
  packagedDate: { name: 'Packaged Date', key: 'theme.packaged-date' },
  useByDate: { name: 'Use By Date', key: 'theme.use-by-date' },
  sellByDate: { name: 'Sell By Date', key: 'theme.sell-by-date' },
  shelfLifeDate: { name: 'Shelf Life Date', key: 'theme.shelf-life-date' },
  expirationDate: { name: 'Expiration Date', key: 'theme.expiration-date' },
  bestByDate: { name: 'Best By Date', key: 'theme.best-by-date' },
  harvestDate: { name: 'Harvest Date', key: 'theme.harvest-date' },
  cultivationDate: { name: 'Cultivation Date', key: 'theme.cultivation-date' },
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const slugifyThemeName = (name: string) => {
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || 'variable';
};

export const buildThemeVariableKey = (name: string, existingKeys: string[] = []) => {
  const base = `theme.${slugifyThemeName(name)}`;
  if (!existingKeys.includes(base)) return base;
  let counter = 2;
  let candidate = `${base}.${counter}`;
  while (existingKeys.includes(candidate)) {
    counter += 1;
    candidate = `${base}.${counter}`;
  }
  return candidate;
};

export const ensureDateThemeVariable = (
  variables: ThemeVariable[] = [],
  dateKind: DateKind,
  id: string,
): { variables: ThemeVariable[]; variable: ThemeVariable } => {
  const existing = variables.find((variable) => variable.generated && variable.dateKind === dateKind);
  if (existing) return { variables, variable: existing };

  const config = DATE_KIND_CONFIG[dateKind];
  const key = buildThemeVariableKey(
    config.key.replace(/^theme\./, ''),
    variables.map((variable) => variable.key),
  );
  const variable: ThemeVariable = {
    id,
    name: config.name,
    key,
    type: 'date',
    value: '',
    dateKind,
    generated: true,
  };
  return { variables: [...variables, variable], variable };
};

const collectKeys = (value: unknown, keys: Set<string>) => {
  if (typeof value === 'string') {
    for (const match of value.matchAll(/{{\s*([^{}]+?)\s*}}/g)) keys.add(match[1]);
    if (value.startsWith('theme.')) keys.add(value);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectKeys(item, keys));
    return;
  }
  if (value && typeof value === 'object') {
    Object.values(value).forEach((item) => collectKeys(item, keys));
  }
};

export const getUsedGeneratedDateVariables = (
  elements: LabelElement[] = [],
  variables: ThemeVariable[] = [],
) => {
  const keys = new Set<string>();
  collectKeys(elements, keys);
  return variables.filter((variable) =>
    variable.generated && variable.dateKind && keys.has(variable.key));
};

export const parseThemeVariableValue = (raw: string | number | boolean, type: ThemeVariableType) => {
  if (type === 'boolean') {
    if (typeof raw === 'boolean') return raw;
    if (typeof raw === 'number') return raw !== 0;
    const normalized = String(raw).trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
  }
  if (type === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }
  return typeof raw === 'string' ? raw : String(raw ?? '');
};

export const themeVariablesToDataMap = (vars: ThemeVariable[] = []) => {
  return vars.reduce<Record<string, string | number | boolean>>((acc, variable) => {
    acc[variable.key] = parseThemeVariableValue(variable.value, variable.type);
    return acc;
  }, {});
};

export const themeVariablesToExamples = (vars: ThemeVariable[] = []) => {
  return vars.reduce<Record<string, string>>((acc, variable) => {
    const value = variable.value;
    if (value === null || value === undefined) {
      acc[variable.key] = '';
    } else if (typeof value === 'string') {
      acc[variable.key] = value;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      acc[variable.key] = String(value);
    } else {
      acc[variable.key] = '';
    }
    return acc;
  }, {});
};

export const replaceVariableKeyInText = (text: string, oldKey: string, newKey: string) => {
  if (!text) return text;
  const pattern = new RegExp(`{{\\s*${escapeRegExp(oldKey)}\\s*}}`, 'g');
  return text.replace(pattern, `{{${newKey}}}`);
};

export const removeVariableKeyFromText = (text: string, key: string) => {
  if (!text) return text;
  const pattern = new RegExp(`{{\\s*${escapeRegExp(key)}\\s*}}`, 'g');
  const normalized = text.replace(pattern, '').replace(/\s{2,}/g, ' ').trim();
  return normalized.endsWith(':') ? normalized.slice(0, -1).trim() : normalized;
};
